/**
 * Execution Template DTO
 * Represents a saved, reusable input+options set for one Execute action
 */
export interface ExecutionTemplate {
  executionTemplateID: number;
  executionScope: 'Process' | 'ProcessThread' | 'ProcessElement';
  scopeReferenceID: number;

  name: string;
  description?: string;
  longDescription?: string;
  displayName?: string;
  slug: string;
  iconUrl?: string;
  imageUrl?: string;

  configuration: string; // JSON blob: { inputData: { items[] }, renderOptions: { forms[] }, templates: { item } }
  concurrencyBehavior: 'Allow' | 'Block';

  // Backs Process_ExecutionTemplates.DefaultTriggerMode (INT, matches
  // BizFirst.Ai.Process.Domain.Enums.ExecutionTemplateTriggerMode):
  // 1 = Execute (fire-and-forget), 2 = ExecuteAndTrack (live SignalR tracking),
  // 3 = ChatNow (opens in a new window). DB default is 1. Typed loosely (not just `1 | 2 | 3`)
  // because this API host's global JsonStringEnumConverter may serialize it as the enum's string
  // name instead — see `resolveExecutionTemplateTriggerMode`'s own doc comment, which reads either
  // form defensively rather than trusting this field's shape blindly.
  defaultTriggerMode?: 1 | 2 | 3 | string | null;

  lastExecutedAt?: string; // ISO 8601 datetime
  executionCount: number;
  isActive: boolean;
  enabled: boolean;

  // Foreign key to Process_ExecutionTemplateTypes (nullable in DB)
  executionTemplateTypeID?: number | null;
  // Foreign key to Process_ExecutionTemplateCategories (nullable in DB)
  executionTemplateCategoryID?: number | null;

  // Standard audit fields
  tenantID: number;
  deleted: boolean;
  archived: boolean;
  lastModifiedOn: string;
  lastModifiedBy?: number;
  createdOn: string;
  createdBy?: number;
  resID: string; // GUID
}

export interface CreateExecutionTemplateRequest {
  executionScope: 'Process' | 'ProcessThread' | 'ProcessElement';
  scopeReferenceID: number;
  name: string;
  description?: string;
  longDescription?: string;
  displayName?: string;
  slug: string;
  iconUrl?: string;
  imageUrl?: string;
  configuration: string;
  concurrencyBehavior?: 'Allow' | 'Block';
  defaultTriggerMode?: 1 | 2 | 3 | string | null;
  executionTemplateTypeID?: number | null;
  executionTemplateCategoryID?: number | null;
}

export type ExecutionTemplateTriggerModeName = 'Execute' | 'ExecuteAndTrack' | 'ChatNow';

const TRIGGER_MODE_BY_NUMBER: Record<number, ExecutionTemplateTriggerModeName> = {
  1: 'Execute',
  2: 'ExecuteAndTrack',
  3: 'ChatNow',
};

/**
 * Resolves `TemplatesPage`'s three-way trigger behavior off `DefaultTriggerMode`. Read defensively
 * as either the numeric form (this type's own `1 | 2 | 3`) or the enum's string member name — this
 * API host's global `JsonStringEnumConverter` (confirmed in
 * `PlatformWebServerExtensionsByBuilder.cs`) serializes enum-typed properties as their name, e.g.
 * `"ExecuteAndTrack"`, so the wire form may not actually match this file's own numeric typing;
 * kept compatible with both rather than assuming one is right. Falls back to `'Execute'`
 * (today's only behavior for this app — a plain Execute button, no Chat Now here) when the value is
 * missing or unrecognized, so a template lacking this data behaves exactly as it did before.
 */
export function resolveExecutionTemplateTriggerMode(
  defaultTriggerMode: unknown
): ExecutionTemplateTriggerModeName {
  if (typeof defaultTriggerMode === 'number') {
    return TRIGGER_MODE_BY_NUMBER[defaultTriggerMode] ?? 'Execute';
  }
  if (typeof defaultTriggerMode === 'string') {
    const normalized = defaultTriggerMode.trim().toLowerCase();
    const named = (['Execute', 'ExecuteAndTrack', 'ChatNow'] as const).find(
      (name) => name.toLowerCase() === normalized
    );
    if (named) return named;
    const asNumber = Number(defaultTriggerMode);
    if (Number.isFinite(asNumber)) return TRIGGER_MODE_BY_NUMBER[asNumber] ?? 'Execute';
  }
  return 'Execute';
}

export interface UpdateExecutionTemplateRequest {
  executionTemplateID: number;
  name?: string;
  description?: string;
  longDescription?: string;
  displayName?: string;
  slug?: string;
  iconUrl?: string;
  imageUrl?: string;
  configuration?: string;
  concurrencyBehavior?: 'Allow' | 'Block';
  defaultTriggerMode?: 1 | 2 | 3 | string | null;
  enabled?: boolean;
  executionTemplateTypeID?: number | null;
  executionTemplateCategoryID?: number | null;
}
